import { AlertCircle, Check, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { JobFileUploadItem } from '@/hooks/useJobFiles';

// What a running batch looks like.
//
// The headline count is phase 1 alone, because phase 1 is when a file becomes
// usable. Originals draining behind it get a quiet line rather than a bar: they
// are best-effort, and a file whose original never lands is complete as far as
// anyone using it is concerned.
//
// The detail lists are split out below because the mobile dock shows the same
// rows behind a tap while the desktop panel shows them inline. One batch, two
// shapes — but only one place that decides what a failure looks like.

interface JobFileUploadProgressProps {
  items: JobFileUploadItem[];
  onRetry: (id: string) => void;
  onRetryAll: () => void;
}

/** A failed original is not a failed file — hence the two lists. */
function isOriginalOnlyFailure(item: JobFileUploadItem): boolean {
  return item.status === 'done' && item.originalStatus === 'failed';
}

export interface JobFileUploadSummary {
  total: number;
  /** Phase 1 completions — what "uploaded" means to someone waiting on a file. */
  uploaded: number;
  failed: JobFileUploadItem[];
  originalFailed: JobFileUploadItem[];
  originalsInFlight: number;
  inFlight: JobFileUploadItem[];
  /** Anything with a retry against it, phase 1 or phase 2. */
  hasFailures: boolean;
}

export function summarizeUploads(items: JobFileUploadItem[]): JobFileUploadSummary {
  const failed = items.filter((item) => item.status === 'failed');
  const originalFailed = items.filter(isOriginalOnlyFailure);

  return {
    total: items.length,
    uploaded: items.filter((item) => item.status === 'done').length,
    failed,
    originalFailed,
    originalsInFlight: items.filter(
      (item) => item.originalStatus === 'pending' || item.originalStatus === 'uploading',
    ).length,
    // Finished successes are left out: the count already speaks for them.
    inFlight: items.filter((item) => item.status === 'pending' || item.status === 'uploading'),
    hasFailures: failed.length > 0 || originalFailed.length > 0,
  };
}

interface JobFileUploadDetailsProps {
  summary: JobFileUploadSummary;
  onRetry: (id: string) => void;
}

/** The per-file rows, without the headline — both layouts render these. */
export function JobFileUploadDetails({ summary, onRetry }: JobFileUploadDetailsProps) {
  const { inFlight, failed, originalFailed, originalsInFlight } = summary;

  return (
    <>
      {inFlight.length > 0 && (
        <ul className="space-y-1">
          {inFlight.map((item) => (
            <li key={item.id} className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-400" />
              <span className="truncate">{item.file.name}</span>
            </li>
          ))}
        </ul>
      )}

      {failed.length > 0 && (
        <ul className="space-y-1">
          {failed.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-2 text-sm">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-500" />
              <span className="truncate font-medium text-gray-700">{item.file.name}</span>
              <span className="text-red-600">{item.error ?? 'Upload failed'}</span>
              <Button
                variant="outline"
                size="sm"
                aria-label={`Retry ${item.file.name}`}
                onClick={() => onRetry(item.id)}
              >
                Retry
              </Button>
            </li>
          ))}
        </ul>
      )}

      {originalFailed.length > 0 && (
        <ul className="space-y-1">
          {originalFailed.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-2 text-sm">
              {/* Deliberately not an error icon: the file uploaded and works. */}
              <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
              <span className="truncate font-medium text-gray-700">{item.file.name}</span>
              <span className="text-gray-500">uploaded — the full-quality copy didn't upload</span>
              <Button
                variant="outline"
                size="sm"
                aria-label={`Retry ${item.file.name}`}
                onClick={() => onRetry(item.id)}
              >
                Retry
              </Button>
            </li>
          ))}
        </ul>
      )}

      {originalsInFlight > 0 && (
        <p className="text-xs text-gray-400">
          Saving full-quality copies, {originalsInFlight} remaining — you can carry on.
        </p>
      )}
    </>
  );
}

export function JobFileUploadProgress({ items, onRetry, onRetryAll }: JobFileUploadProgressProps) {
  if (items.length === 0) return null;

  const summary = summarizeUploads(items);

  return (
    <div className="space-y-2 rounded-lg border bg-white p-3" aria-live="polite">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-700">
          {summary.uploaded} of {summary.total} uploaded
        </span>
        {summary.hasFailures && (
          <Button variant="outline" size="sm" onClick={onRetryAll}>
            Retry all failed
          </Button>
        )}
      </div>

      <JobFileUploadDetails summary={summary} onRetry={onRetry} />
    </div>
  );
}
